import dayjs from "dayjs";

/**
 * Notifications older than this many days collapse into a single "earlier" bucket.
 */
export const EARLIER_THRESHOLD_DAYS = 7;

/**
 * Group notifications (expected newest-first from the API) into dated buckets
 * for the notifications page. Recent items are bucketed by calendar day —
 * with Today/Yesterday shortcuts — and anything older than a week collapses
 * into a single "earlier" bucket.
 *
 * Each returned group carries a `bucket` the caller maps to an i18n label
 * ("today" | "yesterday" | "earlier"), or a preformatted `dateLabel` when
 * `bucket === "date"`. Group order follows the first appearance of each key,
 * preserving the source (newest-first) ordering.
 *
 * @param {Array<{ created_at: string | number | Date }>} notifications
 * @returns {Array<{ key: string, bucket: string, dateLabel: string | null, items: Array }>}
 */
export const groupNotificationsByDate = (notifications = []) => {
  const today = dayjs().startOf("day");
  const groups = [];
  const indexByKey = new Map();

  notifications.forEach((notification) => {
    const date = dayjs(notification.created_at);
    const diffDays = today.diff(date.startOf("day"), "day");

    let key;
    let bucket;
    let dateLabel = null;

    if (diffDays > EARLIER_THRESHOLD_DAYS) {
      key = "earlier";
      bucket = "earlier";
    } else if (diffDays === 0) {
      key = "today";
      bucket = "today";
    } else if (diffDays === 1) {
      key = "yesterday";
      bucket = "yesterday";
    } else {
      key = date.format("YYYY-MM-DD");
      bucket = "date";
      dateLabel = date.format("DD MMM YYYY");
    }

    if (indexByKey.has(key)) {
      groups[indexByKey.get(key)].items.push(notification);
    } else {
      indexByKey.set(key, groups.length);
      groups.push({ key, bucket, dateLabel, items: [notification] });
    }
  });

  return groups;
};
